/**
 * WebSocketService — Reusable real-time connection to the Central Command API.
 *
 * This service is not tied to any specific feature.  It manages a single
 * WebSocket connection per browser session, authenticates via the MSAL Bearer
 * token, and broadcasts incoming messages as a typed RxJS observable.
 *
 * Any feature that needs real-time server push (preferences sync, live
 * notifications, etc.) should inject this service and filter `messages$` by
 * the `type` field of the arriving message.
 *
 * Usage
 * -----
 *   // Subscribe to all messages
 *   this.wsService.messages$.subscribe(msg => { ... });
 *
 *   // Send a message to the server
 *   this.wsService.send({ type: 'ping' });
 *
 *   // Connect (called automatically by UserPreferencesService on login)
 *   this.wsService.connect();
 */

import { Injectable, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Subject, Observable, timer, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SilentRequest } from '@azure/msal-browser';
import { StatusResponse } from '@common';

// ── Message types ─────────────────────────────────────────────────────────────

export interface WsMessageBase {
  type: string;
}

export interface WsConnectedMessage extends WsMessageBase {
  type: 'connected';
  userId: string;
}

export interface WsPreferencesUpdatedMessage extends WsMessageBase {
  type: 'preferences:updated';
  payload: Record<string, unknown>;
}

export type WsProcessLogStatusMessage = WsMessageBase & StatusResponse & {
  type: 'process-log:status_update';
};

/** Extend this union as new WebSocket message types are added. */
export type WsMessage =
  | WsConnectedMessage
  | WsPreferencesUpdatedMessage
  | WsProcessLogStatusMessage
  | WsMessageBase;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Converts an HTTP(S) base URL to its WebSocket equivalent. */
function toWsUrl(apiBaseUrl: string): string {
  if (!apiBaseUrl) {
    // Same-origin deployment: derive from the current page's host.
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${protocol}://${window.location.host}/ws`;
  }
  // eslint-disable-next-line prefer-template -- readability
  return apiBaseUrl.replace(/^https/, 'wss').replace(/^http/, 'ws') + '/ws';
}

// ── Service ───────────────────────────────────────────────────────────────────

const RECONNECT_DELAY_MS = 5_000;
const MAX_RECONNECT_ATTEMPTS = 10;

@Injectable({ providedIn: 'root' })
export class WebSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectSub: Subscription | null = null;
  private destroyed = false;

  private readonly _messages$ = new Subject<WsMessage>();

  /** Observable that emits every message received from the server. */
  readonly messages$: Observable<WsMessage> = this._messages$.asObservable();

  private readonly wsUrl = toWsUrl(environment.apiBaseUrl);

  constructor(private readonly msalService: MsalService) {}

  // ── Connection management ───────────────────────────────────────────────────

  /**
   * Opens the WebSocket connection.  Acquires the current MSAL token silently
   * and passes it as a query param so the server can authenticate the socket.
   *
   * Safe to call multiple times — a no-op if a connection is already open.
   */
  connect(): void {
    if (this.destroyed) {
      return;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return;
    }

    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      console.warn('[WebSocketService] No active MSAL account — skipping connect.');
      return;
    }

    const request: SilentRequest = {
      account,
      scopes: ['api://b843d78c-a646-42c7-9c7b-7ad8828d160e/API.Access'],
    };

    this.msalService
      .acquireTokenSilent(request)
      .subscribe({
        next: (result) => this._openSocket(result.accessToken),
        error: (err) => {
          console.warn('[WebSocketService] Token acquisition failed:', err);
          this._scheduleReconnect();
        },
      });
  }

  /**
   * Sends a message to the server.  No-ops if the socket is not currently open.
   */
  send(message: WsMessageBase): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Permanently closes the connection and stops any pending reconnect.
   * Called automatically by Angular on service destruction.
   */
  disconnect(): void {
    this.destroyed = true;
    this._cancelReconnect();
    if (this.socket) {
      this.socket.onclose = null; // Suppress the reconnect logic on deliberate close
      this.socket.close();
      this.socket = null;
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this._messages$.complete();
  }

  // ── Internal helpers ────────────────────────────────────────────────────────

  private _openSocket(token: string): void {
    const url = `${this.wsUrl}?token=${encodeURIComponent(token)}`;
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this._cancelReconnect();
    };

    this.socket.onmessage = (event: MessageEvent<string>) => {
      try {
        const message = JSON.parse(event.data) as WsMessage;
        this._messages$.next(message);
      } catch {
        console.warn('[WebSocketService] Received non-JSON message:', event.data);
      }
    };

    this.socket.onerror = (event) => {
      console.error('[WebSocketService] Socket error:', event);
    };

    this.socket.onclose = () => {
      this.socket = null;
      if (!this.destroyed) {
        this._scheduleReconnect();
      }
    };
  }

  private _scheduleReconnect(): void {
    if (this.destroyed || this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    this._cancelReconnect();
    this.reconnectAttempts++;
    const delay = RECONNECT_DELAY_MS * Math.min(this.reconnectAttempts, 4);

    this.reconnectSub = timer(delay).subscribe(() => this.connect());
  }

  private _cancelReconnect(): void {
    if (this.reconnectSub) {
      this.reconnectSub.unsubscribe();
      this.reconnectSub = null;
    }
  }
}
